import { MagnifyingGlass } from '@phosphor-icons/react';
import { useDebounce, useSessionStorageState } from 'ahooks';
import { useEffect, useState } from 'react';
import { CopyableField } from 'src/components/CopyableField';
import { Input } from 'src/components/Input';
import Table from 'src/components/Table';
import { useNotification } from 'src/contexts/NotificationContext';
import { useRightHandSidebar } from 'src/contexts/RightHandSidebarContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, Subject } from 'src/hooks/usePermissions';
import { Agent } from 'src/interfaces/agent.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { getFirstAndLastFourUUID } from 'src/utils/number';
import { SimulationTestingSidebar } from './components/SimulationTestingSidebar';

const headers = [
  { key: 'display_id', label: 'ID', width: '8rem' },
  { key: 'label', label: 'Name', width: '30%' },
  { key: 'inbound', label: 'Inbound', width: '5%', disableSorting: true },
  { key: 'action', label: '', width: '30%', disableSorting: true },
];

type Filter = {
  source: string;
  name: string;
  page: number;
  size: number;
  sort_column?: string;
  sort_desc?: boolean;
};

export const SimulationTesting = () => {
  const { get } = useAuthedRequest();
  const notification = useNotification();
  const { openSidebar } = useRightHandSidebar();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [size, setSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useSessionStorageState<Filter>(
    '@control-plane/agent-filters',
    {
      defaultValue: {
        source: '',
        name: '',
        page: 1,
        size: 10,
        sort_column: undefined,
        sort_desc: undefined,
      },
    },
  );
  const debouncedFilter = useDebounce(filters, { wait: 500 });

  const fetchData = async (page: number, perPage: number) => {
    setLoading(true);

    const queryObject: QueryObject = {
      page: String(page),
      size: String(perPage),
      filters: JSON.stringify({
        source: filters?.source,
        name: filters?.name,
      }),
    };

    if (filters?.sort_column) {
      queryObject.sort_column = filters?.sort_column;
      queryObject.sort_desc = filters?.sort_desc ? 'true' : 'false';
    }

    const query = new URLSearchParams(queryObject as Record<string, string>);

    const data = await get(`/agents?${query.toString()}`);

    const items = data.items.map((item: Agent) =>
      Object({
        ...item,
        source: item.user_id ? 'User' : 'Fluents',
        display_id: (
          <CopyableField value={String(item.id)} notification={notification} />
        ),
        name:
          item.name || item.label || getFirstAndLastFourUUID(String(item.id)),
      }),
    );

    setRows(items);
    setTotalItems(data.total);
    setLoading(false);
  };

  useEffect(() => {
    if (!loading) {
      setCurrentPage(filters?.page ?? 1);
      setSize(filters?.size ?? 10);
      fetchData(filters?.page ?? 1, filters?.size ?? 10);
    }
  }, [debouncedFilter]);

  const handlePageChange = (page: number, perPage: number) => {
    setFilters(
      (prev) =>
        ({
          ...prev,
          page,
          size: perPage,
        }) as Filter,
    );
    setCurrentPage(page);
    setSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setFilters(
      (prev) =>
        ({
          ...prev,
          page: 1,
          size: perPage,
        }) as Filter,
    );
    setSize(perPage);
    setCurrentPage(1);
    fetchData(1, perPage);
  };

  const handleSort = (key: string, direction: string) => {
    setFilters(
      (prev) =>
        ({
          ...prev,
          sort_column: key,
          sort_desc: direction === 'desc',
        }) as Filter,
    );
  };

  return (
    <Can operation="read" subject={Subject.AGENTS}>
      <div className="flex flex-col md:flex-row justify-between mb-6">
        <div className="flex w-full md:w-1/2 self-end">
          <div className="flex w-full md:w-96 self-end">
            <Input
              type="text"
              required
              placeholder="Search agents"
              value={filters?.name}
              onChange={(e) =>
                setFilters(
                  (prev) =>
                    ({
                      ...prev,
                      name: e.target.value,
                    }) as Filter,
                )
              }
              icon={<MagnifyingGlass size={20} />}
              iconPosition="left"
              inputAction={() => fetchData(currentPage, size)}
              disabled={loading}
            />
          </div>
        </div>
      </div>

      <Table
        headers={headers}
        rows={rows}
        totalItems={totalItems}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        onSort={handleSort}
        loading={loading}
        setSize={handleSizeChange}
        defaultSize={size}
        onRowClick={(index: number) => {
          const agent = rows[index] as Agent;
          openSidebar(
            <SimulationTestingSidebar agent={agent} />,
            <div className="mx-3 my-2">
              <h3>Agent Simulation Testing</h3>
              <p className="text-sm text-neutral-500">{agent.name}</p>
            </div>,
            '550px',
          );
        }}
      />
    </Can>
  );
};
